import React, { useCallback } from "react";
import DynamicLinesDialog from "../../../components/DynamicLinesDialog";
import {
    fetchDataEngine,
    graphqlQuery,
} from "../../../data/Axios/DynamicService";
import { accountsmanagement } from "../../../config";
import { allAccountSubCategories, allTaxRates } from "../../../data/Axios/queries";

const base_url = accountsmanagement.uri;

const FeeNoteDetailDialog = ({ open, onClose, feeNoteData, refetchData, allowEditing = true }) => {
    // Options loaders
    const loadAccountSubCategories = useCallback(async () => {
        const data = await graphqlQuery(allAccountSubCategories, base_url);
        // Filter for IDs 34 and 37 as in original FeeNoteLinesForm
        return (data || []).filter((subCat) => [34, 37].includes(subCat.id));
    }, []);

    const loadTaxRates = useCallback(async () => {
        const data = await graphqlQuery(allTaxRates, base_url);
        // Filter for taxTypeId = 1 as in original FeeNoteLinesForm
        return (data || []).filter((tax) => tax.taxTypeId === 1);
    }, []);

    // Custom calculation for VAT amount
    const calculateVatAmount = useCallback((formValues, fieldOptions) => {
        const { amount, vatId } = formValues;
        const taxRates = fieldOptions.vatId || [];

        if (!amount || !vatId) return { vatAmount: 0 };

        const selectedTax = taxRates.find((tax) => tax.id === parseInt(vatId));
        if (!selectedTax) return { vatAmount: 0 };

        const vatAmount = (parseFloat(amount) * (selectedTax.rate / 100)).toFixed(2);
        return { vatAmount: parseFloat(vatAmount) };
    }, []);

    // Form fields configuration
    const formFields = [
        {
            name: "description",
            label: "Description",
            type: "text",
            required: true,
        },
        {
            name: "accountSubCategoryId",
            label: "Account Subcategory",
            type: "select",
            required: true,
        },
        {
            name: "amount",
            label: "Amount",
            type: "number",
            required: true,
            onChangeEffect: "calculateVat",
        },
        {
            name: "vatId",
            label: "VAT",
            type: "select",
            required: true,
            onChangeEffect: "calculateVat",
        },
        {
            name: "vatAmount",
            label: "VAT Amount",
            type: "number",
            disabled: true,
            required: false,
        },
    ];

    // Table columns configuration
    const columns = [
        { field: "id", headerName: "ID", flex: 0.5 },
        { field: "description", headerName: "Description", flex: 2 },
        {
            field: "accountSubCategoryName",
            headerName: "Account Subcategory",
            flex: 1.2
        },
        {
            field: "amount",
            headerName: "Amount",
            flex: 1,
            format: "currency"
        },
        {
            field: "vatAmount",
            headerName: "VAT Amount",
            flex: 1,
            format: "currency"
        },
    ];

    // Query configuration
    const queryConfig = {
        name: "allFeeNotesLinesByFeeNoteId",
        modelName: "feeNotesLines",
        inputField: "feeNoteId",
        fields: [
            "id",
            "description",
            "accountSubCategoryId",
            "accountSubCategoryName",
            "amount",
            "vatId",
            "vatAmount",
            "isActive",
        ],
    };

    // Mutation configuration
    const mutationConfig = {
        create: "createFeeNotesLines",
        update: "updateFeeNotesLine",
        modelName: "feeNotesLines",
    };

    // Options loaders map
    const optionsLoaders = {
        accountSubCategoryId: loadAccountSubCategories,
        vatId: loadTaxRates,
    };

    // Custom calculations map
    const customCalculations = {
        calculateVat: calculateVatAmount,
    };

    // Read-only info for edit mode
    const readOnlyInfo = {
        fields: [
            {
                label: "Fee Note",
                getValue: (row) => feeNoteData?.documentNumber || "N/A",
            },
        ],
    };

    return (
        <DynamicLinesDialog
            open={open}
            onClose={onClose}
            title={`Fee Note Lines – ${feeNoteData?.documentNumber || ""}`}
            parentData={feeNoteData}
            parentIdField="feeNoteId"
            baseUrl={base_url}
            queryConfig={queryConfig}
            mutationConfig={mutationConfig}
            refetchData={refetchData}
            formFields={formFields}
            columns={columns}
            optionsLoaders={optionsLoaders}
            customCalculations={customCalculations}
            additionalMutationData={{ isActive: 1 }}
            readOnlyInfo={readOnlyInfo}
            canAdd={true}
            allowEditing={allowEditing}
        />
    );
};

export default FeeNoteDetailDialog;